import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import 'bootstrap/dist/css/bootstrap.css';
import './assets/main.css';
import './assets/styles/typography.css';
import 'bootstrap/dist/js/bootstrap.js';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { auth0 } from './auth/auth0';
import { installFunctionAuthInterceptor } from './auth/functionAuthInterceptor';
import { registerServiceWorker } from './registerServiceWorker';
import { trace, traceBootCount, redactUrl } from './utils/authTrace';
import { initDebugConsole } from './utils/debugConsole';
import { resetOrgResolutionOnFreshLogin } from './utils/resetOrgResolutionOnFreshLogin';
import { recordBoot } from './utils/swWatchdog';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isBetween from 'dayjs/plugin/isBetween';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isLeapYear from 'dayjs/plugin/isLeapYear';
import relativeTime from 'dayjs/plugin/relativeTime';

// A2 recon (2026-08-22 "stale shiftDuration and cost" brief) — every one of
// these plugins was previously registered only inside a lazy-loaded route
// chunk (DashboardView.vue, ToggleShifts.vue, CalendarCopyWeek.vue,
// ViewDashboardShifts.vue, Calendar.vue, ExportTimesheets.vue, Comms.vue),
// never globally. dayjs.extend() mutates the shared dayjs prototype, so a
// component that calls e.g. .isSameOrAfter() without one of those chunks
// having loaded first throws "is not a function" — confirmed for
// ReportAttendanceEmployee.vue/ReportAttendanceLocation.vue/
// ReportLeaveManagement.vue, reachable via a direct deep link to /reports
// that bypasses the usual Dashboard-first navigation. Registering globally
// here, once, at boot removes the load-order dependency entirely. The
// per-file dayjs.extend() calls are left in place — calling extend twice
// with the same plugin is a harmless no-op, and removing eight call sites
// carries more risk than it saves.
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);
dayjs.extend(weekday);
dayjs.extend(weekOfYear);
dayjs.extend(isLeapYear);
dayjs.extend(relativeTime);

// The canonical host (2026-11-05, src/utils/canonicalHost.js). On the hosting
// default domains (<project>.web.app / .firebaseapp.com) the app does not
// install Auth0, mount or register the service worker: it replaces the
// location with the same path on VUE_APP_CANONICAL_ORIGIN. The rest of boot
// still runs (the debug trace, the boot counter in that origin's storage);
// none of it signs in, renders or installs anything.
import { canonicalRedirectTarget } from './utils/canonicalHost';
const canonicalTarget = canonicalRedirectTarget(window.location.href, process.env.VUE_APP_CANONICAL_ORIGIN);
if (canonicalTarget) window.location.replace(canonicalTarget);

// Z-campaign Phase 1, Defect B (2026-08-09) — recorded before anything else
// runs, debug-independent (see swWatchdog.js), so a boot that crashes
// before reaching any of the debug-gated tracing below still counts toward
// the dead-man's-switch threshold checked later in registerServiceWorker.js.
recordBoot();

// Dispatch Brief v2, Phase 2 (2026-08-05) — earliest possible point to
// capture boot state: whether this load is carrying an Auth0 redirect
// callback (?code=&state=), and whether a service worker already controls
// this page (vs. this being a fresh registration — the SW-reload race under
// investigation only fires on the latter). initDebugConsole() is fired here
// too, unawaited, so eruda is up before auth0/router install proceeds below.
initDebugConsole();
trace('main-entry', {
  bootCount: traceBootCount(),
  url: redactUrl(window.location.href),
  hasCode: window.location.search.includes('code='),
  hasState: window.location.search.includes('state='),
  swControllerExists: 'serviceWorker' in navigator ? !!navigator.serviceWorker.controller : 'n/a',
});

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

// Attach the caller's Auth0 token to org-scoped Cloud Functions requests.
installFunctionAuthInterceptor();

const app = createApp(App)
  .use(router)
  .use(pinia);

// Tri-state org-resolution fix, part 2 (2026-08-09) — must run after Pinia
// is active (above) but before the auth0 plugin's own async install below,
// which is what performs the actual code exchange for a redirect-callback
// landing. See resetOrgResolutionOnFreshLogin.js for why.
resetOrgResolutionOnFreshLogin();

trace('auth0-plugin-installing');
if (!canonicalTarget) app.use(auth0);
trace('auth0-plugin-installed-sync-part-done');

if (!canonicalTarget) app.mount('#app');
trace('app-mounted');

// Phase-A1 (2026-08-04) — purely additive: does not touch auth0/router
// setup above, only runs after mount, and no-ops entirely outside a
// production build (see registerServiceWorker.js).
if (!canonicalTarget) registerServiceWorker();
